import { clsx } from 'clsx';
import { X } from 'lucide-react';
import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';

export const cn = (...parts: unknown[]) => clsx(parts);

/* ------------------------------------------------------------------ Button */
type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:brightness-95',
  secondary: 'bg-accent text-white hover:brightness-110',
  outline: 'border border-border bg-card hover:bg-background',
  ghost: 'hover:bg-background',
  danger: 'bg-danger text-white hover:brightness-95',
  success: 'bg-success text-white hover:brightness-95',
};

export function Button({
  variant = 'primary', size = 'md', className, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' }) {
  return (
    <button
      {...rest}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition disabled:opacity-50 disabled:pointer-events-none',
        size === 'sm' ? 'h-8 px-3 text-xs' : 'h-10 px-4 text-sm',
        VARIANTS[variant],
        className,
      )}
    />
  );
}

/* ------------------------------------------------------------------- Input */
export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cn('h-10 w-full rounded-md border border-border bg-card px-3 text-sm placeholder:text-muted/60', className)} />;
}
export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cn('w-full rounded-md border border-border bg-card p-3 text-sm placeholder:text-muted/60', className)} />;
}
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest} className={cn('h-10 w-full rounded-md border border-border bg-card px-3 text-sm', className)}>{children}</select>;
}
export function Label({ children, htmlFor, className }: { children: ReactNode; htmlFor?: string; className?: string }) {
  return <label htmlFor={htmlFor} className={cn('block text-xs font-medium uppercase tracking-wide text-muted mb-1.5', className)}>{children}</label>;
}

/** Field wrapper that always renders the error message next to the control. */
export function Field({ label, error, hint, children, id }: { label: string; error?: string; hint?: string; children: ReactNode; id?: string }) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div>
      <Label htmlFor={fieldId}>{label}</Label>
      {children}
      {hint && !error ? <p className="mt-1 text-xs text-muted">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs text-danger" role="alert">{error}</p> : null}
    </div>
  );
}

/* -------------------------------------------------------------------- Card */
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('rounded-xl border border-border bg-card', className)}>{children}</div>;
}
export function CardHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border p-5">
      <div>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
      </div>
      {actions}
    </div>
  );
}
export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('p-5', className)}>{children}</div>;
}

/* ------------------------------------------------------------------- Badge */
const BADGE: Record<string, string> = {
  draft: 'bg-muted/10 text-muted border-muted/30',
  in_review: 'bg-warning/10 text-warning border-warning/30',
  approved: 'bg-accent/10 text-accent border-accent/30',
  scheduled: 'bg-primary/10 text-primary border-primary/30',
  published: 'bg-success/10 text-success border-success/30',
  archived: 'bg-muted/10 text-muted border-muted/30',
  admin: 'bg-primary/10 text-primary border-primary/30',
  editor: 'bg-accent/10 text-accent border-accent/30',
  writer: 'bg-muted/10 text-muted border-muted/30',
  invited: 'bg-warning/10 text-warning border-warning/30',
  active: 'bg-success/10 text-success border-success/30',
};
export function Badge({ tone, children, className }: { tone?: string; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium', tone ? BADGE[tone] ?? '' : '', className)}>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ Switch */
export function Switch({ checked, onChange, label, id }: { checked: boolean; onChange: (v: boolean) => void; label?: string; id?: string }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} id={id}
      onClick={() => onChange(!checked)}
      className={cn('relative h-5 w-9 shrink-0 rounded-full transition', checked ? 'bg-success' : 'bg-border')}
    >
      <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all', checked ? 'left-[1.15rem]' : 'left-0.5')} />
      {label ? <span className="sr-only">{label}</span> : null}
    </button>
  );
}

/* -------------------------------------------------------------------- Tabs */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.value} role="tab" aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={cn('rounded-md px-3 py-1.5 text-sm transition', value === t.value ? 'bg-primary/10 text-primary font-medium' : 'text-muted hover:bg-background')}
        >
          {t.label}{typeof t.count === 'number' ? ` (${t.count})` : ''}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- Empty/Load */
export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/50 p-10 text-center">
      <p className="font-display text-lg">{title}</p>
      {description ? <p className="mx-auto mt-1 max-w-md text-sm text-muted">{description}</p> : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-10 text-muted" role="status" aria-live="polite">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ Dialog */
export function Dialog({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-auto mt-20 w-full max-w-2xl px-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h3 className="font-display text-lg">{title}</h3>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></Button>
          </div>
          <div className="p-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- Toast */
interface Toast { id: number; message: string; tone: 'ok' | 'error' }
const ToastCtx = createContext<(message: string, tone?: 'ok' | 'error') => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((message: string, tone: 'ok' | 'error' = 'ok') => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s, { id, message, tone }]);
    setTimeout(() => setItems((s) => s.filter((x) => x.id !== id)), 4000);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cn('rounded-md border px-4 py-2 text-sm shadow-lg', t.tone === 'error' ? 'border-danger/40 bg-danger/10 text-danger' : 'border-success/40 bg-success/10 text-success')}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);
